import { PassportStore, usePassportStore } from "src/stores/Passport.store";
import { useEffect, useState } from "react";
import { action, computed, observable } from "mobx";
import { AsyncStore } from "src/stores";
import { asyncRequest } from "src/decorators";
import { LIST_OF_BANKS } from "src/components/BankSelector";
import { Merchant } from "src/models";
import { eventDispatcher } from "src/helpers/eventEmitter";

class BankInfoState extends AsyncStore<BankInfoState> {
  @observable
  selectedBank = "bca";

  @observable
  bankAccountHolder = "";

  @observable
  bankAccountNumber = "";

  @observable
  submitted = false;

  @observable
  verified = false;

  @observable
  accountNameMismatch = false;

  @observable
  maskedBankName = "";

  @observable
  agreeToDataMismatchPolicy = false;

  @observable
  bankBookUploadUrl = "";

  constructor(public passportStore: PassportStore) {
    super();
    const { outlet } = passportStore;

    this.bankAccountHolder = outlet.isCorporateMerchant
      ? outlet.bankAccountName
      : outlet.idName;
    this.bankAccountNumber = outlet.bankAccountNumber;
    this.selectedBank = outlet.bankCode || this.selectedBank;
    this.bankBookUploadUrl = outlet.json.bank_passbook_url || "";
  }

  @computed
  get invalidBankNumber() {
    return this.submitted && !this.verified;
  }

  @computed
  get isFormValid() {
    if (this.accountNameMismatch) {
      return (
        this.verified &&
        this.agreeToDataMismatchPolicy &&
        !!this.bankBookUploadUrl
      );
    }
    return this.verified;
  }

  @computed
  get disableNextButton() {
    if (this.accountNameMismatch) {
      return !this.agreeToDataMismatchPolicy || !this.bankBookUploadUrl;
    }
    return !this.verified;
  }

  @action
  resetState() {
    this.submitted = false;
    this.verified = false;
    this.accountNameMismatch = false;
    this.agreeToDataMismatchPolicy = false;
  }

  @asyncRequest("validate")
  async validate() {
    this.resetState();

    const response = await this.passportStore.service.validateBankAccount({
      account_name: this.bankAccountHolder,
      account: this.bankAccountNumber,
      bank: this.selectedBank,
    });

    this.submitted = true;
    const verified = (this.verified = response.data.data.valid_bank_account);

    if (verified) {
      this.accountNameMismatch = !response.data.data.valid_account_name;
      this.maskedBankName = response.data.data.masked_account_name || "";
    }

    return {
      verified,
      accountNameMismatch: this.accountNameMismatch,
      maskedBankName: this.maskedBankName,
    };
  }

  @action
  setBankBookUploadUrl(url: string) {
    this.bankBookUploadUrl = url;
  }

  @action
  @asyncRequest("postData")
  async postData(outletId: string) {
    const bankName = LIST_OF_BANKS.find((bank) => {
      return bank[0] === this.selectedBank;
    })![1];
    const response = await this.passportStore.service.postBankAccount(
      outletId,
      {
        bank_account: {
          account_name: this.bankAccountHolder,
          account_no: this.bankAccountNumber,
          bank_code: this.selectedBank,
          bank_name: bankName,
          validated: true,
        },
        bank_passbook_url: this.bankBookUploadUrl,
      }
    );

    this.passportStore.outlet = Merchant.fromJSON(response.data);
  }
}

export const useBankInfoStore = () => {
  const passportStore = usePassportStore();
  const [context] = useState(new BankInfoState(passportStore));

  useEffect(() => {
    eventDispatcher.publish({ action: "ob_bankinfo_opened" });
    // Reset form state on unmount
    return () => context.resetState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return context;
};
